// Save schema (rebuild.md §9.12), validated with zod on load and import, migrated through a version chain.
import { z } from 'zod';

export const TierZ = z.enum(['open', 'rationed', 'permit', 'blackout']);

export const SettingsZ = z.object({
  captions: z.boolean().default(true),
  captionSize: z.enum(['small', 'medium', 'large']).default('medium'),
  captionBackground: z.boolean().default(true),
  voices: z.boolean().default(true),
  voiceVolume: z.number().min(0).max(1).default(0.9),
  musicVolume: z.number().min(0).max(1).default(0.6),
  /** Generated focus loops under gameplay (task 8.5). */
  focusMusic: z.boolean().default(true),
  ambienceVolume: z.number().min(0).max(1).default(0.5),
  sfxVolume: z.number().min(0).max(1).default(0.8),
  reduceMotion: z.enum(['system', 'on', 'off']).default('system'),
  highContrast: z.boolean().default(false),
  textScale: z.number().min(1).max(1.5).default(1),
  dyslexiaFont: z.boolean().default(false),
  videoQuality: z.enum(['auto', '720', '1080', '480']).default('auto'),
  chapterIntros: z.enum(['always', 'first', 'guide', 'never']).default('first'),
  autoplayIntros: z.boolean().default(true),
  relaxedTiming: z.boolean().default(false),
  coachMarks: z.boolean().default(true),
});
export type Settings = z.infer<typeof SettingsZ>;
export const defaultSettings = (): Settings => SettingsZ.parse({});

export const StageResultZ = z.object({
  filedAt: z.string(),
  commendations: z.tuple([z.boolean(), z.boolean(), z.boolean()]),
  mistakes: z.number().int().min(0),
  hints: z.number().int().min(0),
  durationSec: z.number().min(0),
});
export type StageResult = z.infer<typeof StageResultZ>;

export const CheckpointZ = z.object({
  stageId: z.string(),
  tier: TierZ,
  goalIndex: z.number().int().min(0),
  trackers: z.record(z.string(), z.union([z.number(), z.boolean()])),
  /** Seconds played before this checkpoint (for the duration on the debrief). */
  elapsedSec: z.number().min(0).default(0),
});
export type CheckpointSave = z.infer<typeof CheckpointZ>;

export const MondayScoreZ = z.object({
  best: z.number().int().min(0),
  of: z.number().int().min(1),
  at: z.string(),
});
export type MondayScore = z.infer<typeof MondayScoreZ>;

export const SaveV1Z = z.object({
  schema: z.literal(1),
  slot: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  label: z.string().max(40).default(''),
  createdAt: z.string(),
  updatedAt: z.string(),
  tier: TierZ.default('rationed'),
  unlocked: z.array(z.string()).default(['prologue']),
  results: z.record(z.string(), z.record(z.string(), StageResultZ)).default({}),
  checkpoint: CheckpointZ.optional(),
  watched: z.array(z.string()).default([]),
  notebook: z.object({ entries: z.array(z.string()).default([]), drillsDone: z.array(z.string()).default([]) }).default({ entries: [], drillsDone: [] }),
  settings: SettingsZ.default(defaultSettings()),
  classroom: z.boolean().default(false),
  /** Lab Practice sets completed (§4.9), e.g. `lab-flame-a`. */
  practice: z.array(z.string()).default([]),
  /** Monday (§4.9): best score per revision set. */
  monday: z.record(z.string(), MondayScoreZ).default({}),
  /** One-time notices already shown (e.g. `blackout-unlocked`). */
  flags: z.array(z.string()).default([]),
});
export type SaveV1 = z.infer<typeof SaveV1Z>;
export type Save = SaveV1;
export const CURRENT_SCHEMA = 1;

export function newSave(slot: 1 | 2 | 3, now: string): Save {
  return SaveV1Z.parse({ schema: 1, slot, createdAt: now, updatedAt: now });
}

/** Migration chain: each step lifts schema n → n+1. Empty until the first schema change. */
const MIGRATIONS: Record<number, (s: Record<string, unknown>) => Record<string, unknown>> = {};

/** Validate (and migrate) an unknown value into the current save shape; throws on garbage. */
export function parseSave(raw: unknown): Save {
  let s = raw as Record<string, unknown>;
  if (!s || typeof s !== 'object') throw new Error('save is not an object');
  let v = Number(s.schema);
  while (v < CURRENT_SCHEMA) {
    const m = MIGRATIONS[v];
    if (!m) throw new Error(`no migration from schema ${v}`);
    s = m(s);
    v = Number(s.schema);
  }
  return SaveV1Z.parse(s);
}
